import { useState } from 'react';
import { Sparkles, Copy, Check, Wand2 } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { generatePin, type GeneratedPin, type Variant } from '../api/pinGenerator';

const REGIONS = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'AU', label: 'Australia' },
  { code: 'BR', label: 'Brazil' },
  { code: 'JP', label: 'Japan' },
];

const TONES = [
  'default',
  'warm & aspirational',
  'punchy & bold',
  'expert & informative',
  'playful & fun',
  'minimal & sleek',
];

export function PinGeneratorPage() {
  const { token } = useAuth();
  const [seed, setSeed] = useState('');
  const [region, setRegion] = useState('US');
  const [url, setUrl] = useState('');
  const [tone, setTone] = useState(TONES[0]);
  const [result, setResult] = useState<GeneratedPin | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!seed.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await generatePin(
        {
          seed: seed.trim(),
          region,
          url: url.trim() || undefined,
          tone: tone === 'default' ? undefined : tone,
        },
        token,
      );
      setResult(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Generation failed.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <PageHeader
        icon={Wand2}
        title="Pin Generator"
        description="AI-generated Pinterest pin titles and descriptions using real trend data + Pinterest SEO best practices."
      />

      <form
        onSubmit={onSubmit}
        className="mb-6 space-y-4 rounded-lg border border-ink-200 bg-white p-6 shadow-sm"
      >
        <div>
          <label className="mb-1 block text-sm font-medium text-ink-700">
            Topic / seed keyword <span className="text-brand-500">*</span>
          </label>
          <input
            type="text"
            required
            value={seed}
            onChange={(e) => setSeed(e.target.value)}
            placeholder='e.g. "minimalist wedding invitations" or "high protein dinner recipes"'
            className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-700">Region</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {REGIONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-700">Tone</label>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full rounded-md border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
            >
              {TONES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-700">
              Pin URL <span className="font-normal text-ink-400">(optional)</span>
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
              className="w-full rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-ink-500">
            Pulls live Pinterest trend data for this seed + region. Takes ~10 seconds.
          </p>
          <button
            type="submit"
            disabled={loading || !seed.trim()}
            className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            {loading ? 'Generating…' : 'Generate pin content'}
          </button>
        </div>
      </form>

      {error && (
        <div className="mb-6 rounded-md border border-brand-100 bg-brand-50 px-4 py-3 text-sm text-brand-700">
          {error}
        </div>
      )}

      {loading && !result && (
        <div className="rounded-lg border border-dashed border-ink-200 bg-white px-8 py-16 text-center">
          <Sparkles className="mx-auto h-8 w-8 animate-pulse text-brand-500" />
          <p className="mt-3 text-sm font-medium text-ink-700">Generating with Claude…</p>
          <p className="mt-1 text-xs text-ink-500">
            Pulling Pinterest trends, applying SEO rules, drafting variants.
          </p>
        </div>
      )}

      {result && <ResultsPanel result={result} />}
    </>
  );
}

function ResultsPanel({ result }: { result: GeneratedPin }) {
  return (
    <div className="space-y-6">
      <StrategyCard notes={result.notes} keywordsUsed={result.keywordsUsed} />

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-500">
          Titles ({result.titles.length})
        </h2>
        <div className="space-y-3">
          {result.titles.map((t, i) => (
            <VariantCard key={`t${i}`} kind="Title" index={i + 1} variant={t} maxLen={100} />
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-500">
          Descriptions ({result.descriptions.length})
        </h2>
        <div className="space-y-3">
          {result.descriptions.map((d, i) => (
            <VariantCard
              key={`d${i}`}
              kind="Description"
              index={i + 1}
              variant={d}
              maxLen={800}
            />
          ))}
        </div>
      </div>

      <HashtagsCard hashtags={result.suggestedHashtags} />
    </div>
  );
}

function StrategyCard({
  notes,
  keywordsUsed,
}: {
  notes: string;
  keywordsUsed: string[];
}) {
  return (
    <div className="rounded-lg border border-ink-200 bg-ink-50/60 p-5">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">Strategy</h3>
      <p className="mt-2 text-sm text-ink-900">{notes}</p>
      {keywordsUsed.length > 0 && (
        <div className="mt-3">
          <p className="text-xs uppercase tracking-wide text-ink-400">Trending keywords woven in</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {keywordsUsed.map((k) => (
              <span
                key={k}
                className="inline-flex items-center rounded-full bg-white px-2 py-0.5 text-xs font-medium text-ink-700 ring-1 ring-inset ring-ink-200"
              >
                {k}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VariantCard({
  kind,
  index,
  variant,
  maxLen,
}: {
  kind: string;
  index: number;
  variant: Variant;
  maxLen: number;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(variant.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  const len = variant.text.length;
  const overBudget = len > maxLen;

  return (
    <div className="rounded-lg border border-ink-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-brand-500 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
            {kind} #{index}
          </span>
          <span
            className={`text-xs ${overBudget ? 'text-brand-600' : 'text-ink-500'}`}
            title={`${len} / ${maxLen} chars`}
          >
            {len} chars
          </span>
        </div>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1 rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <p className="whitespace-pre-line text-sm text-ink-900">{variant.text}</p>
      <p className="mt-3 text-xs italic text-ink-500">Why: {variant.rationale}</p>
    </div>
  );
}

function HashtagsCard({ hashtags }: { hashtags: string[] }) {
  const [copied, setCopied] = useState(false);
  if (hashtags.length === 0) return null;
  const line = hashtags.map((h) => `#${h}`).join(' ');
  async function copy() {
    await navigator.clipboard.writeText(line);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="rounded-lg border border-ink-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">
          Suggested hashtags
        </h2>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1 rounded-md border border-ink-200 px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              Copy all
            </>
          )}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {hashtags.map((h) => (
          <span
            key={h}
            className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700"
          >
            #{h}
          </span>
        ))}
      </div>
    </div>
  );
}
